import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getWeek, getYear, isEqual, setHours } from 'date-fns';
import localForage from 'localforage';
import {
  getAttendance,
  getMonthlyAttendanceReport,
} from './attendance.actions';
import { AttendanceState, TrackAttendanceModelInput } from './attendance.types';
import { setFulfilledThunkActionStatus, setThunkActionStatus } from '../utils';
import { ClassProgrammeDto } from '@ecdlink/core';

const initialState: AttendanceState = {
  attendance: undefined,
  attendanceTracked: undefined,
  monthlyAttendanceRecordsByUser: {},
  missedAttendance: [],
  classroomAttendanceOverviewReport: [],
};

const attendanceSlice = createSlice({
  name: 'attendance',
  initialState,
  reducers: {
    resetAttendanceState: (state) => {
      state.attendance = initialState.attendance;
      state.attendanceTracked = initialState.attendanceTracked;
      state.missedAttendance = initialState.missedAttendance;
    },
    removeAttendance: (state, action) => {
      const attendanceItemsToRemove = state?.attendance?.filter(
        (item) =>
          item?.attendanceDate === action.payload.attendanceDate &&
          item?.classroomProgrammeId === action.payload.classroomProgrammeId
      );

      if (attendanceItemsToRemove && attendanceItemsToRemove.length > 0) {
        for (const item of attendanceItemsToRemove) {
          const index = state?.attendance?.indexOf(item);
          if (index && index !== -1) {
            state?.attendance?.splice(index, 1);
          }
        }
      }
    },
    removeTrackedAttendance: (state, action) => {
      if (!state.attendanceTracked) return;
      const index = state.attendanceTracked.findIndex(
        (x) =>
          x.attendanceDate === action.payload.attendanceDate &&
          x.classroomProgrammeId === action.payload.classroomProgrammeId
      );
      if (index >= 0) {
        state.attendanceTracked.splice(index, 1);
      }
    },
    trackAttendance: (
      state,
      action: PayloadAction<TrackAttendanceModelInput>
    ) => {
      if (!state.attendanceTracked) state.attendanceTracked = [];

      state.attendanceTracked?.push(action.payload);

      if (!state.attendance) state.attendance = [];

      if (action.payload.attendees) {
        const attendanceDate = new Date(action.payload.attendanceDate);
        const week = getWeek(attendanceDate);

        const year = getYear(attendanceDate);
        for (const attendee of action.payload.attendees) {
          const existingIndex = state.attendance?.findIndex(
            (x) =>
              x.userId === attendee.userId &&
              new Date(x.attendanceDate!).getDate() ===
                new Date(attendanceDate).getDate() &&
              x.year === year &&
              x.classroomProgrammeId === action.payload.classroomProgrammeId
          );

          const input = {
            classroomProgrammeId: action.payload.classroomProgrammeId,
            attendanceDate: action.payload.attendanceDate,
            attended: attendee.attended,
            userId: attendee.userId,
            weekOfYear: week,
            year: year,
          };

          if (existingIndex >= 0) {
            state.attendance[existingIndex] = input;
          } else {
            state.attendance.push(input);
          }
        }
      }
    },
    setMissedAttendance: (
      state,
      action: PayloadAction<ClassProgrammeDto[]>
    ) => {
      state.missedAttendance = action.payload;
    },
  },
  extraReducers: (builder) => {
    setThunkActionStatus(builder, getMonthlyAttendanceReport);
    builder.addCase(getAttendance.fulfilled, (state, action) => {
      const sortedAttendanceData = action.payload?.sort((a, b) =>
        (a.attendanceDate ?? 0) > (b.attendanceDate ?? 0) ? 1 : -1
      );
      state.attendance = sortedAttendanceData;
    });
    builder.addCase(getMonthlyAttendanceReport.fulfilled, (state, action) => {
      setFulfilledThunkActionStatus(state, action);
      state.monthlyAttendanceRecordsByUser[action.meta.arg.userId] =
        action.payload;
    });
  },
});

const { reducer: attendanceReducer, actions: attendanceActions } =
  attendanceSlice;

const attendancePersistConfig = {
  key: 'attendance',
  storage: localForage,
  blacklist: [],
};

export { attendancePersistConfig, attendanceReducer, attendanceActions };
